import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import {
  ApprofondimentoComponent,
  ApprofondimentoCta,
  ApprofondimentoTab,
  ApprofondimentoVantaggio,
  ApprofondimentoStep,
} from '@mic-ac/ui-kit';
import { AuthServiceService } from '../../../../core/services/auth-service/auth-service.service';
import { HOME_ROUTE } from '../../../../app-route-paths';

@Component({
  selector: 'app-approfondimento-page',
  imports: [ApprofondimentoComponent],
  templateUrl: './approfondimento-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApprofondimentoPageComponent {
  readonly #document = inject(DOCUMENT);
  readonly #router = inject(Router);
  readonly #oauth = inject(OAuthService);
  readonly #auth = inject(AuthServiceService);

  readonly isLoggedIn = computed(() =>
    this.#auth.user() !== null || this.#oauth.hasValidAccessToken()
  );

  // ── Hero ──────────────────────────────────────────────────────────────────
  readonly heroTitleLine1 = 'Il censimento nazionale del';
  readonly heroHighlight = 'Patrimonio Culturale';
  readonly heroTitleSuffix = 'digitale';
  readonly heroSubtitle =
    'La <strong>Mappa del Patrimonio</strong> nasce dalla partecipazione volontaria degli istituti culturali italiani: musei, archivi, biblioteche e altri luoghi della cultura che scelgono di rendere visibili le proprie collezioni digitali, contribuendo a costruire una mappatura nazionale condivisa. È un primo passo verso una visione d\'insieme del patrimonio culturale digitale in Italia: i dati pubblicati sono una risorsa per la ricerca, per la progettazione di nuovi servizi culturali e per un uso più efficiente delle risorse pubbliche dedicate alla digitalizzazione.';
  readonly heroNote = ''
  readonly heroCtas: ApprofondimentoCta[] = [
    { label: 'Come contribuire', classe: 'secondary', actionId: 'scroll-contribuisci' },
    { label: 'Esplora la Mappa', classe: 'primary', actionId: 'esplora' },
  ];

  // ── Vantaggi ─────────────────────────────────────────────────────────────
  readonly vantaggiTitle = 'I Vantaggi';
  readonly vantaggiTitleSuffix = 'della Mappa';
  readonly vantaggi: ApprofondimentoVantaggio[] = [
    {
      title: 'Accesso unificato alle informazioni',
      description:
        'Raccoglie in un unico ambiente i dati sulle collezioni digitali provenienti da più fonti, favorendo l\'accesso e semplificando la consultazione.',
    },
    {
      title: 'Fruizione interattiva',
      description:
        'Permette di esplorare il patrimonio digitale con strumenti di ricerca avanzata e navigazione geografica intuitiva.',
    },
    {
      title: 'Visibilità del digitale pregresso e sommerso',
      description:
        'Sostiene la ricognizione del digitale pregresso e delle collezioni conservate localmente. Il percorso di rilevazione offre agli enti l\'occasione di verificare, segnalare e descrivere risorse digitali non ancora censite, catalogate o accessibili online.',
    },
    {
      title: 'Interoperabilità e riuso',
      description:
        'Favorisce la collaborazione tra enti e organizzazioni abilitando lo scambio e il riuso dei dati.',
    },
  ];

  // ── Tabs (verticali) ─────────────────────────────────────────────────────
  readonly tabs: ApprofondimentoTab[] = [
    {
      id: 'cosa-trovare',
      label: 'Cosa puoi trovare',

      activeClass: 'bg-primary-400 text-white',
      inactiveClass: 'bg-[#F7F7F7] text-primary-400',
      panelClass: 'bg-[#F7F7F7] text-[#4A4A4A]',
      iconClass: 'bg-primary-400',
      arrowIconUrl: './assets/icons/Arrow-right.svg',

      items: [
        {
          iconUrl: './assets/icons/map-vert-tab.svg',
          title: 'Collezioni digitali del patrimonio culturale',
          description:
            'La Mappa raccoglie informazioni su fondi, raccolte e beni nativi digitali o digitalizzati nel corso di campagne di acquisizione condotte da musei, archivi, biblioteche, parchi archeologici, istituti culturali e altri luoghi della cultura.',
        },
        {
          iconUrl: './assets/icons/spark-vert-tab.svg',
          title: 'Dati sulle collezioni',
          description:
            'Sono disponibili dati su enti, luoghi di conservazione, tipologia dei beni, formati digitali, modalità di accesso e collegamenti con il patrimonio fisico.',
        },
        {
          iconUrl: './assets/icons/doc-vert-tab.svg',
          title: 'Fonti integrate',
          description:
            'Le informazioni provengono da I.PaC – infrastruttura e servizi digitali per il patrimonio culturale, SHARE. Pratiche di cultura al digitale e dall\'attività di censimento svolta con la partecipazione degli enti culturali.',
          descriptionHtml:
            'Le informazioni provengono da <a href="https://ecomic.cultura.gov.it/ipac/" target="_blank" rel="noopener noreferrer">I.PaC – infrastruttura e servizi digitali per il patrimonio culturale</a>, <a href="https://www.fondazionescuolapatrimonio.it/share-pratiche-cultura-digitale/" target="_blank" rel="noopener noreferrer">SHARE. Pratiche di cultura al digitale</a> e dall\'attività di censimento svolta con la partecipazione degli enti culturali.',
        },
      ],
    },
    {
      id: 'come-funziona',
      label: 'Come funziona',
      activeClass: 'bg-primary-400 text-white',
      inactiveClass: 'bg-[#F7F7F7] text-primary-400',
      panelClass: 'bg-secondary-400 text-white',
      iconClass: 'bg-primary-250',
      dividerClass: 'divide-border',
      arrowIconUrl: './assets/icons/Arrow-right.svg',

      items: [
        {
          iconUrl: './assets/icons/combine-vert-tab.svg',
          title: 'Mappa interattiva e ricerca',
          description:
            'La piattaforma consente l\'esplorazione geografica delle collezioni, la ricerca testuale e l\'uso di filtri avanzati per affinare i risultati.',
        },
        {
          iconUrl: './assets/icons/rename-vert-tab.svg',
          title: 'Schede risultato e back-office',
          description:
            'Le schede di dettaglio raccontano le collezioni; tramite il back-office, gli enti possono inserire, aggiornare e validare le informazioni in autonomia.',
        },
        {
          iconUrl: './assets/icons/reuse-vert-tab.svg',
          title: 'Interoperabilità e riuso',
          description:
            'I dati sono disponibili tramite API, GeoJSON e widget integrabili in siti e applicazioni esterne, in linea con i principi dell\'open data.',
        },
      ],
    },
  ];

  // ── Chi può contribuire ──────────────────────────────────────────────────
  readonly contribuisciTitle = 'Chi può contribuire alla Mappa del Patrimonio';
  readonly contribuisciDescription =
    'Possono contribuire gli <strong>enti culturali titolari o gestori</strong> di collezioni digitali, tramite uno <strong>strumento dedicato</strong> per <strong>censire e aggiornare</strong> i propri dati.';
  readonly contribuisciCta = computed<ApprofondimentoCta>(() =>
    this.isLoggedIn()
      ? { label: 'Collezioni', classe: 'primary', actionId: 'go-my-collections' }
      : { label: 'Contribuisci', classe: 'primary', actionId: 'go-login' }
  );

  // ── Steps ────────────────────────────────────────────────────────────────
  readonly stepsTitle = 'Come contribuire - passo per passo';
  readonly steps: ApprofondimentoStep[] = [
    {
      description: 'Aderisci al servizio seguendo la procedura guidata.',
      iconUrl: './assets/icons/Icon.svg',
    },
    {
      description: 'Scarica, compila e invia il modulo di abilitazione.',
      iconUrl: './assets/icons/Icon-1.svg',
    },
    {
      description: 'Attendi la conferma dell\'abilitazione al servizio.',
      iconUrl: './assets/icons/Icon-2.svg',
    },
    {
      description: 'Avvia le attività di censimento tramite lo strumento dedicato.',
      iconUrl: './assets/icons/Icon-3.svg',
    },
  ];

  icon = signal('assets/icons/MANO.svg');

  // ── Crediti ───────────────────────────────────────────────────────────────
  readonly creditiText =
    "La <strong>mappa del patrimonio</strong> è un progetto realizzato grazie ai servizi abilitanti di <a href=\"https://ecomic.cultura.gov.it/\" target=\"_blank\" rel=\"noopener noreferrer\" style=\"color:inherit;text-decoration:underline;\"><strong>Ecomic – ecosistema digitale per la cultura</strong></a>, utilizzando le tecnologie della <a href=\"https://ecomic.cultura.gov.it/dpaas/\" target=\"_blank\" rel=\"noopener noreferrer\" style=\"color:inherit;text-decoration:underline;\"><strong>piattaforma laboratorio DPaaS – piattaforma data product as a service</strong></a>, implementata dall'<a href=\"https://digitallibrary.cultura.gov.it/\" target=\"_blank\" rel=\"noopener noreferrer\" style=\"color:inherit;text-decoration:underline;\"><strong>Istituto centrale per la digitalizzazione del patrimonio culturale – Digital Library</strong></a>.";
    readonly creditiIcon = 'assets/icons/map-monument.svg';

  // ── Navigation ────────────────────────────────────────────────────────────
  onCtaClick(actionId: string): void {
    switch (actionId) {
      case 'scroll-contribuisci':
        this.scrollToContribuisciSection();
        break;
      case 'go-login':
        this.#router.navigate(['/register']);
        break;
      case 'go-my-collections':
        this.#router.navigate(['/mp/my-area']);
        break;
      case 'esplora':
        this.#router.navigate([HOME_ROUTE]);
        break;
    }
  }

  private scrollToContribuisciSection(): void {
    const headings = Array.from(this.#document.querySelectorAll('dpac-approfondimento h2'));
    const targetHeading = headings.find((heading) => {
      const text = heading.textContent?.toLowerCase() ?? '';
      return text.includes('chi può contribuire alla mappa del patrimonio') ||
        text.includes('chi puo contribuire alla mappa del patrimonio');
    });

    const targetSection = targetHeading?.closest('section') as HTMLElement | null;
    if (!targetSection) {
      return;
    }

    const navbarOffset = 124;
    const view = this.#document.defaultView;
    const scrollTop = view?.scrollY ?? 0;
    const top = targetSection.getBoundingClientRect().top + scrollTop - navbarOffset - 12;

    view?.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }


  showModal(): void {
    window.dispatchEvent(new CustomEvent('open-contact-form'));
  }

}



